import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { APP_NAME } from "../helpers/constants";
import Logo from "../components/general/Logo";

function preflightReportUrl() {
  const env = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  if (env) return `${env}/preflight/report`;
  if (typeof window !== "undefined") {
    return `${window.location.origin}/.netlify/functions/api/preflight/report`;
  }
  return "http://localhost:4000/preflight/report";
}

function readSecretFromUrlOnce() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("secret")?.trim() ?? "";
}

/**
 * Operator-only deployment diagnostics. Pass APP_SECRET as ?secret= (URL-encoded);
 * it is sent only in the X-App-Secret header. The query is removed after read.
 */
export default function PreflightPage() {
  const navigate = useNavigate();
  const strippedUrlRef = useRef(false);
  const [credential] = useState(() => readSecretFromUrlOnce());
  const [loading, setLoading] = useState(() => !!readSecretFromUrlOnce());
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  const runChecks = useCallback(async (secret) => {
    const trimmed = String(secret || "").trim();
    if (!trimmed) {
      setError("Missing secret. Open this page as /preflight?secret=YOUR_APP_SECRET");
      setLoading(false);
      return;
    }
    setError("");
    setReport(null);
    setLoading(true);
    try {
      const res = await fetch(preflightReportUrl(), {
        method: "GET",
        headers: { "X-App-Secret": trimmed },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || res.statusText || "Request failed");
      }
      setReport(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!credential || strippedUrlRef.current) return;
    strippedUrlRef.current = true;
    navigate("/preflight", { replace: true });
  }, [credential, navigate]);

  useEffect(() => {
    if (!credential) {
      setLoading(false);
      return;
    }
    runChecks(credential);
  }, [credential, runChecks]);

  const overallOk = report?.ok === true;
  const checks = report?.checks;
  const optionalGapCount = (checks?.optionalEnv || []).filter((r) => !r.ok)
    .length;
  const stripePrices = checks?.stripePrices;
  const stripeGaps =
    stripePrices?.vars?.filter((r) => !r.ok).length ?? 0;

  return (
    <Page>
      <Helmet>
        <title>Preflight · {APP_NAME}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Panel>
        <Hero>
          <BrandBar>
            <Logo
              size="large"
              color="white"
              fullColorMark
              linkTo="/"
              displayMode="both"
              hideIconFrame
            />
            <OperatorTag>Environment · API diagnostics</OperatorTag>
          </BrandBar>
          <Title>Deployment preflight</Title>
          <IntroShell>
            <Lead>
              Pass <code>APP_SECRET</code> in the query string:{" "}
              <code>/preflight?secret=…</code> (use{" "}
              <code>encodeURIComponent(secret)</code> if it contains special characters).
              The secret is sent only as the <code>X-App-Secret</code> header to{" "}
              <code>/preflight/report</code> and is not stored. Do not share links
              that contain the secret.
            </Lead>
          </IntroShell>
        </Hero>

        {!credential ? (
          <Callout $warn>
            No <code>secret</code> in the URL. Add{" "}
            <code>?secret=YOUR_APP_SECRET</code> and reload.
          </Callout>
        ) : null}

        {loading ? (
          <Callout>
            <LoadingLine>
              <Spinner aria-hidden />
              Running checks against the API…
            </LoadingLine>
          </Callout>
        ) : null}
        {error ? <Err role="alert">{error}</Err> : null}

        {report ? (
          <>
            <SummaryCard>
              <SummaryMain>
                <StatusBadge $ok={overallOk}>
                  {overallOk ? "Critical path OK" : "Critical path failed"}
                </StatusBadge>
                {optionalGapCount > 0 ? (
                  <OptionalNote>
                    {optionalGapCount} optional env gap
                    {optionalGapCount === 1 ? "" : "s"}. See Optional environment.
                  </OptionalNote>
                ) : null}
                {stripePrices && stripeGaps > 0 ? (
                  <OptionalNote>
                    {stripeGaps} Stripe plan price
                    {stripeGaps === 1 ? "" : "s"} missing. See Stripe pricing.
                  </OptionalNote>
                ) : null}
              </SummaryMain>
              <Meta>
                <MetaBlock>
                  <MetaLabel>Generated</MetaLabel>
                  <MetaValue>{report.generatedAt || "n/a"}</MetaValue>
                </MetaBlock>
                <MetaBlock>
                  <MetaLabel>Node</MetaLabel>
                  <MetaValue>{report.nodeEnv ?? "n/a"}</MetaValue>
                </MetaBlock>
              </Meta>
            </SummaryCard>

            <Grid>
              <Card>
                <CardTitle>Firebase Admin</CardTitle>
                <StatusLine $ok={checks?.firebaseAdmin?.ok}>
                  {checks?.firebaseAdmin?.ok ? "Configured" : "Incomplete"}
                </StatusLine>
                <CheckList as="div">
                  {(checks?.firebaseAdmin?.vars || []).map((v) => (
                    <CheckRow key={v.key}>
                      <Dot $ok={v.ok} />
                      <RowLabel as="code">{v.key}</RowLabel>
                      <Badge $ok={v.ok}>{v.ok ? "OK" : "Missing"}</Badge>
                    </CheckRow>
                  ))}
                </CheckList>
              </Card>

              <Card>
                <CardTitle>Optional environment</CardTitle>
                <Muted>
                  Features and integrations. Red rows are absent on the API.
                </Muted>
                <CheckList as="div">
                  {(checks?.optionalEnv || []).map((row) => (
                    <EnvRow key={row.key}>
                      <Dot $ok={row.ok} />
                      <EnvText>
                        <span>{row.label}</span>
                        <EnvKey>{row.key}</EnvKey>
                      </EnvText>
                      <Badge $ok={row.ok}>{row.ok ? "OK" : "Missing"}</Badge>
                    </EnvRow>
                  ))}
                </CheckList>
              </Card>

              {stripePrices ? (
                <Card>
                  <CardTitle>Stripe pricing</CardTitle>
                  <Muted>
                    Five <code>STRIPE_PRICE_*</code> IDs: four subscription tiers
                    (Professional and Institutional, monthly and annual) plus{" "}
                    <code>STRIPE_FLAT_49_PRICE_ID</code> for the one-time $49
                    homeowner search fee.
                  </Muted>
                  <StatusLine $ok={stripePrices.ok}>
                    {stripePrices.ok
                      ? "All Stripe prices configured"
                      : "Incomplete: one or more Stripe price IDs missing"}
                  </StatusLine>
                  <CheckList as="div">
                    {(stripePrices.vars || []).map((row) => (
                      <EnvRow key={row.key}>
                        <Dot $ok={row.ok} />
                        <EnvText>
                          <span>{row.label}</span>
                          <EnvKey>{row.key}</EnvKey>
                        </EnvText>
                        <Badge $ok={row.ok}>{row.ok ? "OK" : "Missing"}</Badge>
                      </EnvRow>
                    ))}
                  </CheckList>
                </Card>
              ) : (
                <Card>
                  <CardTitle>Stripe pricing</CardTitle>
                  <Muted>
                    Deploy the latest API to see the{" "}
                    <code>STRIPE_PRICE_*</code> checks in this report.
                  </Muted>
                </Card>
              )}

              <Card>
                <CardTitle>Build-time (CRA) on API</CardTitle>
                <Muted>
                  Whether <code>REACT_APP_*</code> vars exist in the Functions runtime
                  (informational).
                </Muted>
                <CheckList as="div">
                  {(checks?.buildTimeEnv || []).map((row) => (
                    <CheckRow key={row.key}>
                      <Dot $ok={row.presentInRuntime} />
                      <RowLabel as="code">{row.key}</RowLabel>
                      <Badge $ok={row.presentInRuntime}>
                        {row.presentInRuntime ? "Present" : "Absent"}
                      </Badge>
                    </CheckRow>
                  ))}
                </CheckList>
              </Card>

              <Card>
                <CardTitle>Firestore</CardTitle>
                <StatusLine $ok={checks?.firestore?.ok}>
                  {checks?.firestore?.ok
                    ? `Reachable · ${checks.firestore.ms ?? "?"} ms`
                    : checks?.firestore?.error || "Unreachable"}
                </StatusLine>
              </Card>

              <Card $wide>
                <CardTitle>Firestore indexes (repo file)</CardTitle>
                <StatusLine $ok={checks?.firestoreIndexes?.ok}>
                  {checks?.firestoreIndexes?.ok
                    ? `${checks.firestoreIndexes.compositeCount ?? 0} composite definitions`
                    : checks?.firestoreIndexes?.reason || "Unknown"}
                </StatusLine>
                {checks?.firestoreIndexes?.sourcePath ? (
                  <PathLine>{checks.firestoreIndexes.sourcePath}</PathLine>
                ) : null}
              </Card>
            </Grid>

            {Array.isArray(report.hints) && report.hints.length > 0 ? (
              <Hints>
                <CardTitle>Hints</CardTitle>
                <ul>
                  {report.hints.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </Hints>
            ) : null}

            <RawToggle>
              <details>
                <summary>Raw JSON</summary>
                <Pre>{JSON.stringify(report, null, 2)}</Pre>
              </details>
            </RawToggle>

            {credential ? (
              <Actions>
                <RunButton
                  type="button"
                  onClick={() => runChecks(credential)}
                  disabled={loading}
                >
                  {loading ? "Running…" : "Re-run checks"}
                </RunButton>
              </Actions>
            ) : null}
          </>
        ) : null}
      </Panel>
    </Page>
  );
}

const Page = styled.main`
  min-height: calc(100vh - 2rem);
  padding: 2rem 1rem 4rem;
  background: var(--color-primary-dark, #0f172a);
  color: #fafafa;
  font-family: "Inter", "Inter Tight", ui-sans-serif, system-ui, sans-serif;
`;

const Panel = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Hero = styled.header`
  width: 100%;
  margin-bottom: 1.5rem;
`;

const BrandBar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 1.35rem;
`;

const OperatorTag = styled.p`
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const Title = styled.h1`
  width: 100%;
  text-align: center;
  font-size: 1.35rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 0 0 1.1rem;
  color: #fafafa;
`;

const IntroShell = styled.div`
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  padding: 1.15rem 1.25rem 1.2rem;
  border: 1px solid #262626;
  background: #111;
  box-shadow: var(--shadow-light, 2px 2px 0 0 #1e293b);
`;

const Lead = styled.p`
  font-size: 0.9rem;
  line-height: 1.65;
  color: #cbd5e1;
  margin: 0 auto;
  max-width: 44rem;
  text-align: center;

  code {
    font-size: 0.86em;
    font-family: var(--font-stack-mono, ui-monospace, monospace);
    color: #e2e8f0;
    background: rgba(30, 41, 59, 0.6);
    padding: 0.1em 0.35em;
  }
`;

const Callout = styled.div`
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${(p) => (p.$warn ? "#b45309" : "#262626")};
  background: ${(p) => (p.$warn ? "#1c1410" : "#111")};
  color: ${(p) => (p.$warn ? "#fde68a" : "#d4d4d4")};
  font-size: 0.9rem;
  box-shadow: ${(p) =>
    p.$warn ? "2px 2px 0 0 rgba(180, 83, 9, 0.35)" : "var(--shadow-light, 2px 2px 0 0 #1e293b)"};

  code {
    font-size: 0.88em;
    font-family: var(--font-stack-mono, ui-monospace, monospace);
    color: #fafafa;
  }
`;

const LoadingLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid #404040;
  border-top-color: var(--color-action, #ff5722);
  border-radius: 50%;
  animation: preflight-spin 0.7s linear infinite;

  @keyframes preflight-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Err = styled.div`
  color: #fecaca;
  border: 1px solid #7f1d1d;
  background: #1a0a0a;
  padding: 0.65rem 0.85rem;
  margin: 0 0 1rem;
  font-size: 0.85rem;
`;

const SummaryCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid #262626;
  background: #111;
  box-shadow: var(--shadow-medium, 3px 3px 0 0 #1e293b);
`;

const SummaryMain = styled.div`
  flex: 1;
  min-width: 200px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  font-weight: 800;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 2px solid
    ${(p) => (p.$ok ? "var(--color-financial, #00c853)" : "var(--color-error, #e11900)")};
  color: ${(p) => (p.$ok ? "#bbf7d0" : "#fecaca")};
  background: ${(p) => (p.$ok ? "#052e16" : "#450a0a")};
  box-shadow: ${(p) =>
    p.$ok
      ? "var(--shadow-success, 3px 3px 0 0 #00c853)"
      : "var(--shadow-emergency, 3px 3px 0 0 #e11900)"};
`;

const OptionalNote = styled.p`
  margin: 0.55rem 0 0;
  font-size: 0.8rem;
  color: #fcd34d;
  line-height: 1.45;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.75rem;
  text-align: right;

  @media (max-width: 640px) {
    text-align: left;
    width: 100%;
  }
`;

const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const MetaLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #737373;
`;

const MetaValue = styled.span`
  font-size: 0.78rem;
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  color: #e5e5e5;
  max-width: 22rem;
  word-break: break-word;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.section`
  border: 1px solid #262626;
  padding: 1rem 1.15rem;
  background: #111;
  grid-column: ${(p) => (p.$wide ? "1 / -1" : "auto")};
  box-shadow: var(--shadow-light, 2px 2px 0 0 #1e293b);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #e5e5e5;
`;

const Muted = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.8rem;
  color: #94a3b8;
  line-height: 1.5;

  code {
    font-family: var(--font-stack-mono, ui-monospace, monospace);
    font-size: 0.88em;
    color: #cbd5e1;
  }
`;

const StatusLine = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => (p.$ok ? "#bbf7d0" : "#fecaca")};
  margin-bottom: 0.35rem;
`;

const CheckList = styled.div`
  margin-top: 0.65rem;
  max-height: 340px;
  overflow: auto;
  padding-right: 2px;
`;

const CheckRow = styled.div`
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 0.5rem 0.65rem;
  align-items: center;
  font-size: 0.82rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #1f1f1f;
`;

const EnvRow = styled.div`
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 0.5rem 0.65rem;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #1f1f1f;
`;

const EnvText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;

  span:first-child {
    color: #e5e5e5;
    font-weight: 500;
    line-height: 1.35;
  }
`;

const EnvKey = styled.span`
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  font-size: 0.72rem;
  color: #94a3b8;
  letter-spacing: 0.02em;
  word-break: break-all;
`;

const RowLabel = styled.span`
  min-width: 0;
  word-break: break-word;
  color: #d4d4d4;
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  font-size: 0.78rem;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$ok ? "#4ade80" : "#f87171")};
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.45rem;
  border: 1px solid ${(p) => (p.$ok ? "#166534" : "#7f1d1d")};
  color: ${(p) => (p.$ok ? "#bbf7d0" : "#fecaca")};
  white-space: nowrap;
`;

const PathLine = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  color: #94a3b8;
  word-break: break-all;
`;

const Hints = styled.section`
  margin-top: 1.25rem;
  padding: 1rem 1.15rem;
  border: 1px dashed #404040;
  background: #0c0c0c;

  ${CardTitle} {
    margin-bottom: 0.35rem;
  }

  ul {
    margin: 0.5rem 0 0;
    padding-left: 1.15rem;
    color: #cbd5e1;
    font-size: 0.85rem;
    line-height: 1.55;
  }
`;

const RawToggle = styled.div`
  margin-top: 1.25rem;

  summary {
    cursor: pointer;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 0.5rem;
    user-select: none;
  }

  summary:hover {
    color: #e2e8f0;
  }
`;

const Pre = styled.pre`
  margin: 0.5rem 0 0;
  padding: 0.85rem;
  background: #0a0a0a;
  border: 1px solid #262626;
  font-size: 0.68rem;
  overflow: auto;
  max-height: 420px;
  color: #d4d4d4;
  font-family: var(--font-stack-mono, ui-monospace, monospace);
  line-height: 1.45;
`;

const Actions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const RunButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #fafafa;
  background: #fafafa;
  color: #0a0a0a;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.75rem;
  cursor: pointer;
  box-shadow: var(--shadow-light, 2px 2px 0 0 #1e293b);

  &:hover:not(:disabled) {
    background: #f1f5f9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
